import React, { useState } from 'react';

// Components
import { WpIcon } from 'Wpds/components/icon';
import { WpDivider } from 'Wpds/components/divider';
import { WpAvatar } from 'Wpds/components/avatar';
import { WpText } from 'Wpds/components/text';
import { WpButton } from 'Wpds/components/button';
import WpToastManager from 'Wpds/components/toast/WpToastManager';

// Hooks
import { useCurrentUser } from 'Shared/utils/hooks/use-current-user';
import { useCreateOrSendBa } from 'Shared/utils/hooks/use-create-or-send-ba';

// Types
import { User } from 'Shared/constants/types';

// Styles
import './TeamMemberWithoutBAWrapper.scss';

type Props = {
  user: User;
  buttonText: string;
  buttonIcon?: string;
  isPending?: boolean;
};

const TeamMemberWithoutBAWrapper: React.FC<Props> = ({ user, buttonText, buttonIcon, isPending = false }: Props) => {
  const baseClassName = 'TeamMemberWithoutBAWrapper';
  const { user: currentUser } = useCurrentUser();
  const [isProcessing, setIsProcessing] = useState(false);

  const { sendBaEmail, resendBaEmail } = useCreateOrSendBa({
    user,
    isTakingBaForSelf: false,
  });

  const handleButtonClick = async () => {
    setIsProcessing(true);

    try {
      if (isPending) {
        await resendBaEmail();
        WpToastManager.success('Assessment resent.');
      } else {
        await sendBaEmail();
        WpToastManager.success('Assessment sent.');
      }
    } catch (error) {
      WpToastManager.error(
        `Error ${isPending ? 'resending' : 'sending'} assessment to ${user.fullName}. Please try again.`
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__content`}>
        <div className={`${baseClassName}__user-info`}>
          <WpAvatar avatar={user.avatar} size="sm" />
          <div className={`${baseClassName}__name-container`}>
            <WpText tag="span" variant="bodySm">
              {user.fullName}{' '}
              {user.piUserGuid === currentUser.piUserGuid ? (
                <WpText tag="span" variant="bodySm" color="gray-600">
                  (You)
                </WpText>
              ) : null}
            </WpText>
          </div>
        </div>

        <div className={`${baseClassName}__action`}>
          <WpButton
            variant="tertiary"
            size="sm"
            onClick={handleButtonClick}
            disabled={isProcessing}
            loading={isProcessing}
          >
            <WpIcon name={buttonIcon} />
            <WpText variant="bodySm" tag="span" color="gray-600">
              {buttonText}
            </WpText>
          </WpButton>
        </div>
      </div>
      <WpDivider padding={8} />
    </div>
  );
};

export default TeamMemberWithoutBAWrapper;
